import type { EaseTypeImpl, Lerpable } from '../types';
import { catmullRomWeightedDual, cubicBezier, clerp, sampleToCubicBezier, sqr } from '@use-gpu/core';
import { clone, for2, for4, zip2, zip4 } from './zip';

const snapToNearest = (a: number, b: number) => clerp(a, b, 1);

export const EaseAngle: EaseTypeImpl<Lerpable> = {
  spline: (a: Lerpable, b: Lerpable, knots?: Lerpable[]) => {
    if (knots) {
      const [k1, k2] = knots;
      if (!k1 || !k2) throw new Error("Spline ease needs 2 knots");

      // Relative to angle of a
      const ak1 = clone(k1);
      const ak2 = clone(k2);
      const ab = clone(b);

      zip2(ak1, a, k1, snapToNearest);
      zip2(ak2, ak1, k2, snapToNearest);
      zip2(ab, ak2, b, snapToNearest);

      return (target: Lerpable, t: number) =>
        zip4(target, a, ak1, ak2, ab,
          (a: number, k1: number, k2: number, b: number) => {
            return cubicBezier(t, a, k1, k2, b);
          }
        );
    }
    else {
      return (target: Lerpable, t: number) =>
        zip2(target, a, b,
          (a: number, b: number) => clerp(a, b, t)
        );
    }
  },
  auto: (a: Lerpable, b: Lerpable, c: Lerpable, d: Lerpable) => {
    const l1 = EaseAngle.measure(a, b);
    const l2 = EaseAngle.measure(b, c);
    const l3 = EaseAngle.measure(c, d);

    // Relative to angle of b
    const ba = clone(a);
    const bc = clone(c);
    const bd = clone(d);

    zip2(ba, b, a, snapToNearest);
    zip2(bc, b, c, snapToNearest);
    zip2(bd, c, d, snapToNearest);

    const k1 = clone(b);
    const k2 = clone(b);

    const k1s: number[] = [];
    const k2s: number[] = [];

    for4(ba, b, bc, bd,
      (a: number, b: number, c: number, d: number) => {
        const [,k1,k2] = sampleToCubicBezier((t: number) => catmullRomWeightedDual(t, a, b, c, d, l1, l2, l3))
        k1s.push(k1);
        k2s.push(k2);
      }
    );

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    zip2(k1, k1, k1, () => k1s.shift()!);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    zip2(k2, k2, k2, () => k2s.shift()!);

    return [k1, k2];
  },
  lerp: (target: Lerpable, a: Lerpable, b: Lerpable, t: number) => {
    return zip2(target, a, b, (a: number, b: number) => clerp(a, b, t));
  },
  measure: (a: Lerpable, b: Lerpable) => {
    let d = 0;
    for2(a, b, (a: number, b: number) => { d += sqr(a - clerp(a, b, 1)); });
    return Math.sqrt(d);
  },
};
